import { ReactElement, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  TextField,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotate,
  faXmark,
  faChevronDown,
  faChevronRight,
  faTruckFast,
} from "@fortawesome/free-solid-svg-icons";
import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import { BoxHeaderTypography } from "../../components/Typographies/BoxHeaderTypography";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../components/Buttons/SecondaryButton";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { useApiClient } from "../../../data/clients/ApiClient";
import { PageQuery } from "../../../utils/models/pageQuery";
import { SortDirection } from "../../components/GenericTable/Models/sortDirection";
import { PlaceOrderQueueItem } from "../../../core/models/entities/PlaceOrderQueueItem";
import {
  useErrorToast,
  useSuccessToast,
} from "../../components/Toast/Toast";
import { useConfirmationDialog } from "../../components/Dialogs/confirmation-dialog";

const PAGE_SIZE = 25;
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Alle statusser" },
  { value: "Pending", label: "Afventer" },
  { value: "Processing", label: "Behandler" },
  { value: "Completed", label: "Færdig" },
  { value: "Failed", label: "Fejlet" },
];

function statusLabel(status: string): string {
  return (
    STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status
  );
}

function StatusPill({ status }: { status: string }) {
  const styles =
    status === "Failed"
      ? "bg-red-100 text-red-800 border-red-200"
      : status === "Completed"
        ? "bg-green-100 text-green-800 border-green-200"
        : status === "Processing"
          ? "bg-amber-100 text-amber-800 border-amber-200"
          : status === "Pending"
            ? "bg-slate-100 text-slate-700 border-slate-200"
            : "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span
      className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium tabular-nums ${styles}`}
    >
      {statusLabel(status)}
    </span>
  );
}

function formatDateTime(d: Date | undefined): string {
  if (!d) return "—";
  return d.toLocaleString("da-DK", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function tryFormatJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function OrdersQueueScreen(): ReactElement {
  const { placeOrderQueueRepository } = useRepositoriesContext();
  const apiClient = useApiClient();
  const successToast = useSuccessToast;
  const errorToast = useErrorToast;

  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchActive, setSearchActive] = useState<string>("");
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [syncingShipmentDates, setSyncingShipmentDates] = useState(false);

  async function performShipmentDateSync() {
    if (syncingShipmentDates) return;
    setSyncingShipmentDates(true);
    try {
      const updated = await apiClient.syncOrderShipmentDates();
      successToast(`Synkronisering færdig — ${updated} ordrer opdateret.`);
    } catch (e) {
      errorToast(
        e instanceof Error
          ? `Synkronisering fejlede: ${e.message}`
          : "Synkronisering fejlede",
      );
    } finally {
      setSyncingShipmentDates(false);
    }
  }

  const { component: confirmShipmentSyncDialog, open: openConfirmShipmentSync } =
    useConfirmationDialog(
      "Vil du synkronisere afsendelsesdatoer fra Navision nu?\n\n" +
        "Dette henter den seneste forventede afsendelsesdato for alle aktive ordrer fra Navision " +
        "og opdaterer 'Est. Shipment Date' på ordrelisten. Det kan tage et par minutter.",
      () => {
        performShipmentDateSync();
      },
    );

  const query: PageQuery = useMemo(
    () => ({
      searchTerm: searchActive,
      filterBy: statusFilter
        ? [{ field: "status", value: statusFilter }]
        : [],
      sortBy: { field: "createdAtUtc", sortDirection: SortDirection.DESC },
      page: { size: PAGE_SIZE, index: pageIndex },
    }),
    [statusFilter, searchActive, pageIndex],
  );

  const list = placeOrderQueueRepository.useQueueItems(query, {
    refetchInterval: autoRefresh ? 30_000 : undefined,
  });

  const items: PlaceOrderQueueItem[] = list.data?.results ?? [];
  const totalCount = list.data?.totalCount ?? 0;
  const pageCount = list.data?.pageCount ?? 1;

  function applySearch() {
    setSearchActive(searchInput.trim());
    setPageIndex(1);
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <JetSportBox>
        <BoxHeader2 className="border-b">
          <div className="flex items-center justify-between w-full gap-4">
            <BoxHeaderTypography>Ordrekø</BoxHeaderTypography>
            <div className="flex items-center gap-3 text-xs text-slate-600">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
                <span>Auto-opdater (30s)</span>
              </label>
              <button
                type="button"
                onClick={() => list.refetch()}
                className="inline-flex items-center gap-1.5 rounded border border-slate-300 px-2.5 py-1 hover:bg-slate-50"
                title="Opdater nu"
              >
                <FontAwesomeIcon icon={faRotate} />
                <span>Opdater</span>
              </button>
              <button
                type="button"
                onClick={openConfirmShipmentSync}
                disabled={syncingShipmentDates}
                className="inline-flex items-center gap-1.5 rounded border border-slate-300 px-2.5 py-1 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Hent forventede afsendelsesdatoer fra Navision"
              >
                <FontAwesomeIcon
                  icon={syncingShipmentDates ? faRotate : faTruckFast}
                  spin={syncingShipmentDates}
                />
                <span>
                  {syncingShipmentDates
                    ? "Synkroniserer…"
                    : "Synk. afsendelsesdatoer"}
                </span>
              </button>
            </div>
          </div>
        </BoxHeader2>
        <BoxContent className="py-4">
          <div className="flex flex-wrap gap-3 mb-4">
            <Select
              size="small"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPageIndex(1);
              }}
              displayEmpty
              className="min-w-[180px]"
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applySearch();
              }}
              placeholder="Søg efter ordrenr."
              className="min-w-[240px]"
            />
            <SecondaryButton onClick={applySearch}>Søg</SecondaryButton>
            <div className="ml-auto text-xs text-slate-500 self-center">
              {list.isLoading
                ? "Indlæser…"
                : `${totalCount ?? items.length} resultater`}
            </div>
          </div>

          <div className="overflow-x-auto border rounded">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <Th>ID</Th>
                  <Th>Ordrenr.</Th>
                  <Th>Status</Th>
                  <Th>Org</Th>
                  <Th>Oprettet af</Th>
                  <Th>Oprettet</Th>
                  <Th>Opdateret</Th>
                  <Th>Fejl</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !list.isLoading && (
                  <tr>
                    <td
                      colSpan={9}
                      className="text-center text-slate-400 py-8"
                    >
                      Ingen elementer fundet
                    </td>
                  </tr>
                )}
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelectedId(item.id)}
                  >
                    <Td className="tabular-nums">{item.id}</Td>
                    <Td className="font-mono">{item.orderNumber || "—"}</Td>
                    <Td>
                      <StatusPill status={item.status} />
                    </Td>
                    <Td className="tabular-nums">{item.orgId}</Td>
                    <Td className="max-w-[180px] truncate">
                      {item.createdBy || "—"}
                    </Td>
                    <Td className="whitespace-nowrap text-slate-600">
                      {formatDateTime(item.createdAtUtc)}
                    </Td>
                    <Td className="whitespace-nowrap text-slate-600">
                      {formatDateTime(item.updatedAtUtc)}
                    </Td>
                    <Td className="max-w-[260px] truncate text-red-700">
                      {item.status === "Completed"
                        ? ""
                        : (item.errorMessage ?? "")}
                    </Td>
                    <Td>
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="text-slate-400"
                      />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="flex justify-center mt-4">
              <Pagination
                count={pageCount}
                page={pageIndex}
                onChange={(_, p) => setPageIndex(p)}
                size="small"
              />
            </div>
          )}
        </BoxContent>
      </JetSportBox>

      <QueueItemDetailDialog
        id={selectedId}
        onClose={() => setSelectedId(undefined)}
      />
      {confirmShipmentSyncDialog}
    </div>
  );
}

function QueueItemDetailDialog({
  id,
  onClose,
}: {
  id: number | undefined;
  onClose: () => void;
}): ReactElement | null {
  const { placeOrderQueueRepository } = useRepositoriesContext();
  const open = id !== undefined && id > 0;
  const detail = placeOrderQueueRepository.useQueueItem(id ?? 0);
  const item = detail.data;
  const [retrying, setRetrying] = useState(false);
  const [showPayload, setShowPayload] = useState(false);
  const successToast = useSuccessToast;
  const errorToast = useErrorToast;

  async function performRetry() {
    if (!item || item.status !== "Failed") return;
    setRetrying(true);
    try {
      await placeOrderQueueRepository.retry(item.id);
      successToast("Køen er sat til at prøve igen");
      onClose();
    } catch (e) {
      errorToast(
        e instanceof Error
          ? `Genforsøg fejlede: ${e.message}`
          : "Genforsøg fejlede",
      );
    } finally {
      setRetrying(false);
    }
  }

  const { component: confirmRetryDialog, open: openConfirmRetry } =
    useConfirmationDialog(
      "Vil du genstarte denne ordre?\n\n" +
        "Status sættes tilbage til 'Afventer', fejlbeskeden ryddes, og elementet lægges på køen igen til behandling.\n\n" +
        "Brug kun dette hvis ordren ikke blev gennemført i Navision — ellers kan ordren blive oprettet to gange.",
      () => {
        performRetry();
      },
    );

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className="flex items-center justify-between">
        <span>
          Kø-element {item?.id ? `#${item.id}` : ""}
          {item?.orderNumber ? ` · ${item.orderNumber}` : ""}
        </span>
        <IconButton onClick={onClose} size="small">
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {detail.isLoading || !item ? (
          <p className="text-center text-slate-500 py-8">Indlæser…</p>
        ) : (
          <div className="space-y-4 text-sm">
            <DetailGrid item={item} />
            {item.errorMessage && item.status !== "Completed" && (
              <div className="border border-red-200 bg-red-50 rounded p-3">
                <div className="text-[11px] uppercase tracking-wide font-semibold text-red-700 mb-1">
                  Fejlbesked
                </div>
                <pre className="whitespace-pre-wrap text-red-800 text-xs">
                  {item.errorMessage}
                </pre>
              </div>
            )}
            <div className="border border-slate-200 rounded">
              <button
                type="button"
                onClick={() => setShowPayload((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                <span>Vis payload (RequestBody)</span>
                <FontAwesomeIcon
                  icon={showPayload ? faChevronDown : faChevronRight}
                />
              </button>
              {showPayload && (
                <pre className="px-3 py-2 text-[11px] bg-slate-900 text-slate-100 overflow-auto max-h-[400px] rounded-b">
                  {item.requestBody
                    ? tryFormatJson(item.requestBody)
                    : "Ingen payload"}
                </pre>
              )}
            </div>
            {item.status === "Failed" && (
              <p className="text-[11px] text-slate-500 leading-snug">
                Genforsøg sætter status til <strong>Afventer</strong>,
                rydder fejlbeskeden, og lægger elementet tilbage på
                køen til behandling. Brug kun dette hvis ordren ikke
                blev gennemført i Navision.
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <SecondaryButton onClick={onClose}>Luk</SecondaryButton>
              {item.status === "Failed" && (
                <PrimaryButton
                  disabled={retrying}
                  onClick={openConfirmRetry}
                >
                  {retrying
                    ? "Sender…"
                    : "Genstart og læg på kø igen"}
                </PrimaryButton>
              )}
            </div>
          </div>
        )}
      </DialogContent>
      {confirmRetryDialog}
    </Dialog>
  );
}

function DetailGrid({ item }: { item: PlaceOrderQueueItem }): ReactElement {
  const rows: { label: string; value: React.ReactNode }[] = [
    { label: "ID", value: item.id },
    { label: "Ordrenr.", value: item.orderNumber || "—" },
    { label: "Status", value: <StatusPill status={item.status} /> },
    { label: "Org", value: item.orgId },
    { label: "Bruger (B2C)", value: item.userB2CId || "—" },
    { label: "Admin", value: item.isAdmin ? "Ja" : "Nej" },
    { label: "OrderId", value: item.orderId ?? "—" },
    { label: "Oprettet af", value: item.createdBy || "—" },
    { label: "Oprettet", value: formatDateTime(item.createdAtUtc) },
    { label: "Opdateret", value: formatDateTime(item.updatedAtUtc) },
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
      {rows.map((r) => (
        <div key={r.label} className="flex justify-between gap-4">
          <dt className="text-slate-500 text-xs uppercase tracking-wide">
            {r.label}
          </dt>
          <dd className="text-slate-800 text-sm tabular-nums text-right">
            {r.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th className="text-left px-3 py-2 font-medium text-[11px] uppercase tracking-wide">
      {children}
    </th>
  );
}

function Td({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-3 py-2 align-middle ${className}`}>{children}</td>;
}
